import { Menu, Transition } from '@headlessui/react';
import { Fragment, useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import { formatToDate, getEndOfYear, getStartOfYear, mergeClassName } from '@/lib/utils';
import PropTypes from 'prop-types';
import { CustomFormElement, toastType } from '@/components/common';
import ChangeStatusModal from '../ChangeStatusModal';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { StatusDateValidationSchema } from '../StatusDateValidationSchema';
import { onboardingConstants, dashboardConstants } from '@/lib/constants';
import { updateRepsStatusAsync, soSeasonsSelector } from '@/redux/sales-operations';
import { selectIsUpdateStatusLoading } from '@/redux/loading';
import { repStatusesSelector } from '@/redux/reps';
import { isAdminSelector } from '@/redux/auth';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { addToastsAction } from '@/redux/toasts';
import ReactDOM from 'react-dom';

const {
  ACTUAL_END_DATE_LABEL,
  SP_START_DATE_LABEL,
} = onboardingConstants;

const {
  REP_STATUS_NAME,
} = dashboardConstants;

const StatusDropdown = ({
  isDisabled,
  onChange,
  repStatuses,
  updateRepsStatus,
  repsToChangeStatus,
  wrapperClassName,
  label,
  isAdmin,
  addToast,
  isRepStatusLogicEnabled,
  recruitingSeasons,
  recruitingSeasonId,
  usePortal,
}) => {
  const [datePickerName, setDatePickerName] = useState(null);
  const [datePickerLabel, setDatePickerLabel] = useState(null);
  const [datePickerValue, setDatePickerValue] = useState(null);
  const [isStatusModal, setIsStatusModal] = useState(false);
  const [repsStatusCode, setRepsStatusCode] = useState('');
  const [repsStatus, setRepsStatus] = useState(null);
  const isMounted = useRef(true);
  const [menuStyles, setMenuStyles] = useState({});
  const buttonRef = useRef(null);

  useEffect(() => {
    if (usePortal) {
      const handleResize = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const scrollY = window.scrollY;

          setMenuStyles({
            top: `${rect.bottom + 7 + scrollY}px`,
            left: `${rect.left}px`,
          });
        }
      };

      handleResize();
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [usePortal]);

  const currentYear = (new Date()).getFullYear();
  const minDate = getStartOfYear(currentYear - 1);
  const maxDate = getEndOfYear(currentYear + 1);

  const wrapperClasses = useMemo(
    () => classNames(
      wrapperClassName ? wrapperClassName : 'absolute left-4 p-1',
    ),
    [wrapperClassName],
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(StatusDateValidationSchema),
    context: { repStatuses },
  });

  const openStatusModal = useCallback(() => {
    setIsStatusModal(true);
  }, []);

  const closeStatusModal = useCallback(() => {
    clearErrors();
    setIsStatusModal(false);
    setRepsStatusCode('');
  }, [clearErrors]);

  const onUpdateRepsStatus = useCallback((newRepsStatusData) => {
    let unableRepsToChangeStatus = [];

    const newRepStatusCode = newRepsStatusData ? newRepsStatusData[REP_STATUS_NAME] : repsStatusCode;
    const status = repStatuses.find((status) => status.statusCode === newRepStatusCode);

    repsToChangeStatus.map((rep) => {
      if (!isAdmin && !rep.is_coming && status.adminOnly && status.isComing) {
        unableRepsToChangeStatus = [...unableRepsToChangeStatus, rep];
      }
    });

    if (isRepStatusLogicEnabled && unableRepsToChangeStatus.length > 0) {
      const message = 'You can not change status for following reps. Please contact the sales admin.';
      const details = unableRepsToChangeStatus.map((item) => item.name).join(', ');

      addToast([{
        type: toastType.WARNING,
        message: message,
        details: details,
      }]);

      return;
    }

    const statusParams = {
      status: newRepStatusCode,
      reps: repsToChangeStatus.map((rep) => rep.id),
      recruiting_season_id: recruitingSeasonId,
    };

    if (datePickerName) {
      statusParams[datePickerName] = datePickerValue;
    }

    updateRepsStatus({
      statusParams: statusParams,
      onSuccess: () => {
        onChange();
      },
    });

    if (isMounted.current && isStatusModal) {
      closeStatusModal();
    } else if (isMounted.current) {
      setRepsStatusCode('');
    }
  }, [
    repsStatusCode,
    repsToChangeStatus,
    datePickerName,
    updateRepsStatus,
    isStatusModal,
    datePickerValue,
    closeStatusModal,
    onChange,
    repStatuses,
    isAdmin,
    isRepStatusLogicEnabled,
    addToast,
    recruitingSeasonId,
  ]);

  const onChangeDatepickerHandler = useCallback((event) => {
    const { name, value } = event.target;

    setValue(name, value, { shouldValidate: true });
    setDatePickerValue(value);
  }, [setValue]);

  const isCurrentRecruitingSeasonSelected = useMemo(() =>
    recruitingSeasonId === undefined || !!recruitingSeasons.find(({ id }) => id === +recruitingSeasonId)?.is_current, [
    recruitingSeasons,
    recruitingSeasonId,
  ]);

  const changeStatus = (newRepStatusCode) => () => {
    setRepsStatusCode(newRepStatusCode);
    setValue(REP_STATUS_NAME, newRepStatusCode, { shouldValidate: true });

    const status = repStatuses.find((status) => status.statusCode === newRepStatusCode);

    if (status) {
      setRepsStatus(status);
    }

    if (!status || !newRepStatusCode) {
      return;
    }

    if (
      (isCurrentRecruitingSeasonSelected && status.isLossOfAccess)
      || status.shouldBeUpdatedWithStartDate
      || status.shouldBeUpdatedWithEndDate
    ) {
      openStatusModal();
    } else {
      onUpdateRepsStatus({ [REP_STATUS_NAME]: status.statusCode });
    }

    if (status.shouldBeUpdatedWithStartDate) {
      setDatePickerName('start_date');
      setDatePickerLabel(SP_START_DATE_LABEL);
    } else if (status.shouldBeUpdatedWithEndDate) {
      setDatePickerName('actual_end_date');
      setDatePickerLabel(ACTUAL_END_DATE_LABEL);
    } else {
      setDatePickerName(null);
      setDatePickerLabel(null);
    }

    setValue(datePickerName, '', { shouldValidate: false });
    setDatePickerValue(null);
  };

  const menuItems = (
    <Transition
      show={true}
      as={Fragment}
      enter="transition ease-out duration-200"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items
        as="ul"
        className="absolute rounded-lg bg-white shadow-popup z-[1]"
        style={usePortal ? menuStyles : {}}
      >
        {repStatuses.map((repStatus, i) => (
          <Menu.Item as="li" key={repStatus.statusCode}>
            <button
              type="button"
              className={mergeClassName(
                'w-full py-2.5 px-4 text-sm text-left text-gray-600 hover:bg-gray-50 transition-color duration-200',
                { 'border-b': Boolean(repStatuses[i + 1]) },
                { 'whitespace-nowrap': true },
              )}
              onClick={changeStatus(repStatus.statusCode)}
            >
              {repStatus.statusTitle}
            </button>
          </Menu.Item>
        ))}
      </Menu.Items>
    </Transition>
  );

  return (
    <>
      <Menu>
        {({ open }) => (
          <>
            <Menu.Button ref={buttonRef} className={wrapperClasses} disabled={isDisabled}>
              {label ?? ''}
              <ChevronDownIcon className="w-4 h-4 stroke-gray-400" />
            </Menu.Button>
            {open && (usePortal ? ReactDOM.createPortal(menuItems, document.body) : menuItems)}
          </>
        )}
      </Menu>
      <ChangeStatusModal
        isOpened={isStatusModal}
        status={repsStatusCode}
        onCancel={closeStatusModal}
        onAction={handleSubmit(onUpdateRepsStatus)}
        getConfirmationMessage={(status, statusText) => isCurrentRecruitingSeasonSelected && repsStatus?.isLossOfAccess
          ? `By changing the user status to “${statusText}“, this user will not have access to their profile or other information.`
          : ''
        }
        repStatuses={repStatuses}
      >
        {datePickerName && datePickerLabel && (
          <CustomFormElement
            register={register}
            id={datePickerName}
            name={datePickerName}
            label={datePickerLabel}
            formValue={datePickerValue}
            type="date"
            minDate={formatToDate(minDate)}
            maxDate={formatToDate(maxDate)}
            showYearDropdown
            onChange={onChangeDatepickerHandler}
            required
            error={errors?.[datePickerName]}
            disabled={false}
            formElementWrapperClassName="my-2"
          />
        )}
      </ChangeStatusModal>
    </>
  );
};

StatusDropdown.propTypes = {
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func,
  updateRepsStatus: PropTypes.func,
  repStatuses: PropTypes.arrayOf(PropTypes.object),
  wrapperClassName: PropTypes.string,
  repsToChangeStatus: PropTypes.array,
  label: PropTypes.string,
  isAdmin: PropTypes.bool,
  addToast: PropTypes.func,
  isRepStatusLogicEnabled: PropTypes.bool,
  recruitingSeasons: PropTypes.arrayOf(PropTypes.object),
  recruitingSeasonId: PropTypes.string,
  usePortal: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  repStatuses: repStatusesSelector(state),
  isStatusUpdating: selectIsUpdateStatusLoading(state),
  isAdmin: isAdminSelector(state),
  recruitingSeasons: soSeasonsSelector(state),
});

const mapDispatchToProps = {
  updateRepsStatus: updateRepsStatusAsync.request,
  addToast: addToastsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(StatusDropdown);
